/**
 * TourProvider — mounted once in Layout (tour spec §6).
 *
 * Responsibilities:
 *  - Manual launches (Today checklist link / navbar menu / Settings Help card)
 *    via useTourStore — pull entry points always work, even when opted out.
 *  - Auto mini-tours (B/C/D) + single coach marks: fire ONCE on first visit to
 *    their route when their anchors exist (rAF poll, 3s cap), unseen, !optOut.
 *    At most ONE auto thing fires per route visit — never chained (§4).
 *  - Resume snackbar ("Resume the tour? (step n of m)") — offered once per
 *    saved mid-tour exit; declining marks the tour dismissed (§5).
 *  - Skip-confirm on every mid-tour exit with the "Don't show tips again"
 *    checkbox (global optOut — kills auto only) (§5).
 *  - A11y: one aria-live="polite" region announcing "Step n of m: <title>".
 *
 * Instrumentation: trackEvent tour_start / tour_complete / tour_dismiss with
 * {tour, step}; driver buttons + dialog buttons carry data-track attributes
 * for the delegated click listener.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Driver } from 'driver.js';
import { trackEvent } from '../utils/analytics';
import { AUTO_TOUR_PRIORITY, getTour, isCrossRoute, TOURS, type TourDef } from './registry';
import {
  bumpTourSession,
  clearTourResume,
  loadTourState,
  markResumeOffered,
  markTourSeen,
  setTourOptOut,
  setTourResume,
} from './storage';
import { resolveTourLang, STRINGS } from './strings';
import {
  createCrossRouteTour,
  createTourDriver,
  isMobileViewport,
  resolveSteps,
  waitForAnchors,
  type CrossRouteTourController,
} from './useTour';
import { useTourStore } from './tourStore';

interface ActiveTour {
  def: TourDef;
  driver: Driver;
  total: number;
  /** Present for cross-route tours — owns navigation + chapter jumps. */
  ctrl?: CrossRouteTourController;
}

/** Chapter of a step (analytics payloads + the skip-part jump). */
const chapterAt = (def: TourDef, stepIndex: number): string | undefined =>
  def.steps[stepIndex]?.chapter;

interface ConfirmState {
  stepIndex: number;
  total: number;
}

interface SnackbarState {
  tourId: TourDef['id'];
  stepIndex: number;
  total: number;
}

export function TourProvider() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const requested = useTourStore((s) => s.requested);
  const clearRequest = useTourStore((s) => s.clearRequest);
  const bumpStorageVersion = useTourStore((s) => s.bumpStorageVersion);

  const activeRef = useRef<ActiveTour | null>(null);
  // Stable handle for the cross-route engine (navigate's identity can change).
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const sessionsRef = useRef(0);
  const autoAttemptedPathRef = useRef<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const lang = resolveTourLang();
  const chrome = STRINGS[lang].chrome;

  // Count this browser session once (drives the Cmd+K 5th-session gate).
  useEffect(() => {
    sessionsRef.current = bumpTourSession();
  }, []);

  const teardown = useCallback(() => {
    activeRef.current?.ctrl?.cancel(); // kill in-flight anchor waits first
    activeRef.current?.driver.destroy();
    activeRef.current = null;
    setConfirm(null);
    setDontShowAgain(false);
  }, []);

  const startTour = useCallback(
    (tour: TourDef, startAt = 0) => {
      if (activeRef.current) return;

      // ── Cross-route walkthrough ("Show me around") ─────────────────────
      if (isCrossRoute(tour)) {
        const ctrl = createCrossRouteTour({
          tour,
          lang,
          navigate: (p) => navigateRef.current(p),
          onStep: (i, total, title) => {
            setAnnouncement(chrome.announceStep(i + 1, total, title));
          },
          onExitAttempt: ({ stepIndex, isDone }) => {
            const active = activeRef.current;
            if (!active) return;
            if (isDone) {
              const chapter = chapterAt(active.def, stepIndex);
              markTourSeen(active.def.id, 'completed');
              clearTourResume();
              trackEvent('tour_complete', {
                tour: active.def.id,
                step: stepIndex + 1,
                ...(chapter ? { chapter } : {}),
              });
              teardown();
              bumpStorageVersion();
              // End of the walkthrough → land the user back on Today.
              if (window.location.pathname !== '/app') navigateRef.current('/app');
            } else {
              setConfirm({ stepIndex, total: active.total });
            }
          },
        });
        const at = Math.min(Math.max(startAt, 0), ctrl.total - 1);
        activeRef.current = { def: tour, driver: ctrl.driver, total: ctrl.total, ctrl };
        trackEvent('tour_start', {
          tour: tour.id,
          step: at + 1,
          steps: ctrl.total,
          lang,
          ...(chapterAt(tour, at) ? { chapter: chapterAt(tour, at) } : {}),
        });
        ctrl.goTo(at, 1);
        return;
      }

      // ── Same-route mini-tours / coach marks (unchanged) ────────────────
      const steps = resolveSteps(tour);
      if (steps.length === 0) return;
      const at = Math.min(Math.max(startAt, 0), steps.length - 1);
      const d = createTourDriver({
        tour,
        lang,
        steps,
        onStep: (i, total, title) => {
          setAnnouncement(chrome.announceStep(i + 1, total, title));
        },
        onExitAttempt: ({ stepIndex, isDone }) => {
          const active = activeRef.current;
          if (!active) return;
          if (isDone) {
            markTourSeen(active.def.id, 'completed');
            clearTourResume();
            trackEvent('tour_complete', { tour: active.def.id, step: stepIndex + 1 });
            teardown();
            bumpStorageVersion();
          } else {
            // Mid-tour exit → skip-confirm (spec §5). Driver stays active
            // behind the dialog until the user decides.
            setConfirm({ stepIndex, total: active.total });
          }
        },
      });
      activeRef.current = { def: tour, driver: d, total: steps.length };
      trackEvent('tour_start', { tour: tour.id, step: at + 1, steps: steps.length, lang });
      d.drive(at);
    },
    [lang, chrome, teardown, bumpStorageVersion]
  );

  // ── Route / request engine ────────────────────────────────────────────────
  useEffect(() => {
    // A tour can't survive a route change — save the place and fold quietly.
    // NOTE: a cross-route tour's own navigation never lands here — its
    // routeMatch covers every route its steps live on.
    const active = activeRef.current;
    if (active && !active.def.routeMatch(pathname)) {
      const idx = active.driver.getActiveIndex() ?? 0;
      if (active.def.kind === 'tour') setTourResume(active.def.id, idx);
      else markTourSeen(active.def.id, 'dismissed'); // coach marks show once
      trackEvent('tour_dismiss', {
        tour: active.def.id,
        step: idx + 1,
        reason: 'route_change',
        ...(chapterAt(active.def, idx) ? { chapter: chapterAt(active.def, idx) } : {}),
      });
      teardown();
      bumpStorageVersion();
    }

    // A resume snackbar left over from another route folds quietly (it will
    // never be re-offered — resumeOffered was already stamped). Track it as a
    // local so this very effect run can still attempt the new route's autos.
    const liveSnackbar =
      snackbar && getTour(snackbar.tourId).routeMatch(pathname) ? snackbar : null;
    if (snackbar && !liveSnackbar) setSnackbar(null);

    let cancelled = false;

    // 1) Manual request (always wins; overrides seen + optOut — pull never dies).
    if (requested) {
      const tour = getTour(requested.id);
      const targetPath = requested.path;
      if (targetPath && pathname !== targetPath && !tour.routeMatch(pathname)) {
        navigate(targetPath);
        return; // effect re-runs at the new pathname with the request intact
      }
      setSnackbar(null);
      if (isCrossRoute(tour)) {
        // The cross-route engine does its own per-step navigation + anchor
        // waits (and silently skips missing anchors), so no pre-flight
        // waitForAnchors gate — start synchronously, THEN clear the request
        // (same ordering rule as below: startTour must set activeRef before
        // the clearRequest-triggered effect re-run).
        startTour(tour, 0);
        clearRequest();
        return;
      }
      // Clear the request only AFTER the tour starts: clearRequest() changes
      // this effect's `requested?.nonce` dep, which re-runs the effect and
      // fires this run's cleanup — if that happens while waitForAnchors is
      // still pending (it always is; it resolves via rAF), `cancelled` kills
      // the .then and the tour never starts. Leaving the request in place is
      // safe: re-entering this branch just re-arms the same idempotent wait,
      // and startTour guards on activeRef.
      waitForAnchors(tour, 8000).then((ok) => {
        if (cancelled) return;
        clearRequest();
        if (ok) startTour(tour, 0);
      });
      return () => {
        cancelled = true;
      };
    }

    // 2) Auto machinery — at most one push per route visit, never while
    //    anything else is on screen.
    if (activeRef.current || confirm || liveSnackbar) return;
    // Once per route visit — but the ref is stamped only when something
    // actually fires, so a StrictMode-cancelled first pass doesn't eat it.
    if (autoAttemptedPathRef.current === pathname) return;

    const stored = loadTourState();

    // 2a) Resume snackbar — offered once per saved exit (spec §5).
    if (stored.resume && !stored.resumeOffered[stored.resume.tourId]) {
      const tour = TOURS.find((t) => t.id === stored.resume?.tourId);
      if (tour && tour.routeMatch(pathname)) {
        const { stepIndex } = stored.resume;
        autoAttemptedPathRef.current = pathname;
        markResumeOffered(tour.id);
        setSnackbar({ tourId: tour.id, stepIndex, total: tour.steps.length });
        return;
      }
    }

    // 2b) Unseen auto mini-tours / coach marks (skip all when opted out).
    if (stored.optOut) return;
    const mobile = isMobileViewport();
    const candidate = AUTO_TOUR_PRIORITY.map((id) => getTour(id)).find(
      (t) =>
        t.auto &&
        !stored.seen[t.id] &&
        stored.resume?.tourId !== t.id && // resume flow owns interrupted tours
        t.routeMatch(pathname) &&
        (!t.desktopOnly || !mobile) &&
        (!t.minSessions || sessionsRef.current >= t.minSessions)
    );
    if (!candidate) return;

    waitForAnchors(candidate, 3000).then((ok) => {
      if (cancelled || !ok || activeRef.current) return;
      if (autoAttemptedPathRef.current === pathname) return; // parallel poll won
      // Re-check terminal state — another tab may have run it meanwhile.
      if (loadTourState().seen[candidate.id]) return;
      autoAttemptedPathRef.current = pathname;
      startTour(candidate, 0);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, requested?.nonce]);

  // Destroy any live driver on unmount (logout etc.).
  useEffect(() => () => teardown(), [teardown]);

  // ── Skip-confirm handlers ─────────────────────────────────────────────────
  const onKeepGoing = () => setConfirm(null);
  const onStop = () => {
    const active = activeRef.current;
    if (!active) {
      setConfirm(null);
      return;
    }
    const idx = confirm?.stepIndex ?? 0;
    const wasCrossRoute = !!active.ctrl;
    if (active.def.kind === 'tour') setTourResume(active.def.id, idx);
    else markTourSeen(active.def.id, 'dismissed');
    if (dontShowAgain) setTourOptOut(true);
    trackEvent('tour_dismiss', {
      tour: active.def.id,
      step: idx + 1,
      opt_out: dontShowAgain,
      ...(chapterAt(active.def, idx) ? { chapter: chapterAt(active.def, idx) } : {}),
    });
    teardown();
    bumpStorageVersion();
    if (wasCrossRoute) {
      // Land back on Today — and suppress the resume snackbar on THAT
      // arrival (stamping the attempted path skips branch 2a once), so
      // stopping doesn't instantly re-offer what was just declined. The
      // offer still fires on the next tour-route visit.
      autoAttemptedPathRef.current = '/app';
      if (window.location.pathname !== '/app') navigateRef.current('/app');
    }
  };
  // "Skip this part" — jump to the next chapter's first available step
  // (cross-route tours only; the driver stayed active behind the dialog).
  const onSkipPart = () => {
    const active = activeRef.current;
    const idx = confirm?.stepIndex ?? 0;
    const next = active?.ctrl ? active.ctrl.nextChapterIndex(idx) : null;
    setConfirm(null);
    setDontShowAgain(false);
    if (active?.ctrl && next != null) active.ctrl.goTo(next, 1);
  };

  // ── Resume snackbar handlers ──────────────────────────────────────────────
  const onResume = () => {
    if (!snackbar) return;
    const tour = getTour(snackbar.tourId);
    const at = snackbar.stepIndex;
    setSnackbar(null);
    clearTourResume();
    if (isCrossRoute(tour)) {
      // The engine navigates to the saved step's own route and waits there —
      // gating on the CURRENT route's anchors would wrongly drop the resume.
      startTour(tour, at);
      return;
    }
    waitForAnchors(tour, 3000).then((ok) => {
      if (ok) startTour(tour, at);
    });
  };
  const onDeclineResume = () => {
    if (!snackbar) return;
    markTourSeen(snackbar.tourId, 'dismissed');
    clearTourResume();
    trackEvent('tour_dismiss', {
      tour: snackbar.tourId,
      step: snackbar.stepIndex + 1,
      reason: 'resume_declined',
      ...(chapterAt(getTour(snackbar.tourId), snackbar.stepIndex)
        ? { chapter: chapterAt(getTour(snackbar.tourId), snackbar.stepIndex) }
        : {}),
    });
    setSnackbar(null);
    bumpStorageVersion();
  };

  return (
    <>
      {/* aria-live region — announces each step for screen readers (spec §6) */}
      <div aria-live="polite" role="status" className="sr-only">
        {announcement}
      </div>

      {/* Resume snackbar (spec §5) */}
      {snackbar && (
        <div
          role="alertdialog"
          aria-label={chrome.resumePrompt(snackbar.stepIndex + 1, snackbar.total)}
          style={{ zIndex: 1000000002 }}
          className="fixed bottom-20 lg:bottom-6 left-1/2 -translate-x-1/2 w-[calc(100vw-2rem)] max-w-sm bg-legal-900 text-legal-100 border border-accent/60 rounded-xl shadow-elevation-3 px-4 py-3 flex items-center gap-3"
        >
          <p className="text-sm flex-1 min-w-0">
            {chrome.resumePrompt(snackbar.stepIndex + 1, snackbar.total)}
          </p>
          <button
            type="button"
            data-track={`tour-${snackbar.tourId}-resume`}
            onClick={onResume}
            className="shrink-0 text-sm font-semibold text-legal-900 bg-accent hover:bg-accent-400 rounded-lg px-3 py-1.5 transition-colors"
          >
            {chrome.resume}
          </button>
          <button
            type="button"
            data-track={`tour-${snackbar.tourId}-resume-decline`}
            onClick={onDeclineResume}
            className="shrink-0 text-sm text-legal-300 hover:text-white transition-colors"
          >
            {chrome.notNow}
          </button>
        </div>
      )}

      {/* Skip-confirm — every exit carries "Don't show tips again" (spec §5) */}
      {confirm && (
        <div
          style={{ zIndex: 1000000002 }}
          className="fixed inset-0 flex items-end sm:items-center justify-center p-4"
        >
          <button
            type="button"
            aria-label={chrome.keepGoing}
            onClick={onKeepGoing}
            className="absolute inset-0 bg-legal-900/40 cursor-default"
          />
          <div
            role="alertdialog"
            aria-label={chrome.skipTitle}
            className="relative w-full max-w-sm bg-legal-900 text-legal-100 border border-accent/60 rounded-xl shadow-elevation-3 p-5"
          >
            <p className="font-semibold text-white text-[15px]">{chrome.skipTitle}</p>
            <p className="text-sm text-legal-300 mt-1.5">{chrome.skipBody}</p>
            <label className="flex items-center gap-2 mt-4 text-sm text-legal-300 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="h-4 w-4 rounded border-legal-600 accent-[#d69e2e]"
              />
              {chrome.dontShowAgain}
            </label>
            <div className="flex flex-wrap justify-end gap-2 mt-5">
              <button
                type="button"
                data-track="tour-skip-stop"
                onClick={onStop}
                className="px-3.5 py-2 text-sm text-legal-300 hover:text-white border border-legal-700 rounded-lg transition-colors"
              >
                {chrome.stopForNow}
              </button>
              {/* Cross-route walkthrough only: jump to the next chapter */}
              {activeRef.current?.ctrl &&
                activeRef.current.ctrl.nextChapterIndex(confirm.stepIndex) != null && (
                  <button
                    type="button"
                    data-track="tour-skip-part"
                    onClick={onSkipPart}
                    className="px-3.5 py-2 text-sm text-legal-300 hover:text-white border border-legal-700 rounded-lg transition-colors"
                  >
                    {chrome.skipChapter}
                  </button>
                )}
              <button
                type="button"
                data-track="tour-skip-keep-going"
                onClick={onKeepGoing}
                className="px-3.5 py-2 text-sm font-semibold text-legal-900 bg-accent hover:bg-accent-400 rounded-lg transition-colors"
              >
                {chrome.keepGoing}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
